// Contexto GLOBAL de filtros dos relatorios (fundacao da Barra de Contexto).
// A tela declara-se "wired" lendo daqui em vez de manter date-picker proprio.
// Rollout progressivo: telas nao-wired seguem com filtros locais ate migrarem.
// O PERIODO nao persiste entre sessoes: toda visita comeca no padrao (mes
// corrente); a escolha do usuario vale enquanto ele navega pelas abas.
import { reactive, computed, ref } from 'vue';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import subDays from 'date-fns/subDays';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import subMonths from 'date-fns/subMonths';
import startOfQuarter from 'date-fns/startOfQuarter';
import endOfQuarter from 'date-fns/endOfQuarter';
import subQuarters from 'date-fns/subQuarters';
import startOfYear from 'date-fns/startOfYear';

const STORAGE_KEY = 'smart2_reports_context_v1';

// Periodos "ate agora" terminam HOJE (dia parcial incluido), igual ao padrao
// "dia 1 do mes ate o dia atual". Periodos fechados terminam onde acabaram.
export const PERIOD_PRESETS = [
  { key: 'today', range: today => [today, today] },
  { key: 'yesterday', range: today => [subDays(today, 1), subDays(today, 1)] },
  { key: '7d', range: today => [subDays(today, 6), today] },
  { key: '14d', range: today => [subDays(today, 13), today] },
  { key: '30d', range: today => [subDays(today, 29), today] },
  { key: '60d', range: today => [subDays(today, 59), today] },
  { key: '90d', range: today => [subDays(today, 89), today] },
  { key: 'month', range: today => [startOfMonth(today), today] },
  {
    key: 'last_month',
    range: today => [
      startOfMonth(subMonths(today, 1)),
      endOfMonth(subMonths(today, 1)),
    ],
  },
  { key: 'quarter', range: today => [startOfQuarter(today), today] },
  {
    key: 'last_quarter',
    range: today => [
      startOfQuarter(subQuarters(today, 1)),
      endOfQuarter(subQuarters(today, 1)),
    ],
  },
  { key: 'year', range: today => [startOfYear(today), today] },
];

export const DEFAULT_PERIOD = 'month';

const loadPersisted = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const persisted = loadPersisted();

const state = reactive({
  // Periodo NAO vem do storage: comeca sempre no padrao
  periodKey: DEFAULT_PERIOD, // preset key ou 'custom'
  customFrom: null, // 'yyyy-MM-dd'
  customTo: null,
  businessHours: persisted.businessHours || false,
  // Agrupamento temporal global (Agrupar: dia/semana/mes). Como o periodo,
  // NAO vem do storage: toda visita comeca no diario, que e a leitura padrao.
  groupBy: 'day',
  // Escopos globais ({id, name} ou null): telas wired filtram por eles.
  scopes: {
    team: persisted.scopes?.team || null,
    inbox: persisted.scopes?.inbox || null,
    agent: persisted.scopes?.agent || null,
    label: persisted.scopes?.label || null,
    pipeline: persisted.scopes?.pipeline || null,
    // CRM: responsavel, origem (UTM) e status do deal
    owner: persisted.scopes?.owner || null,
    deal_source: persisted.scopes?.deal_source || null,
    deal_status: persisted.scopes?.deal_status || null,
    // Etiqueta DO DEAL (id = titulo, e o que o jsonb guarda) e produto
    deal_label: persisted.scopes?.deal_label || null,
    product: persisted.scopes?.product || null,
    // Tipo de produto: produto/servico ou recorrencia da cobranca
    product_kind: persisted.scopes?.product_kind || null,
  },
});

// Spec dinamica registrada pela pagina (CRM muda por aba interna) — nao
// persiste; o wrapper usa como fallback quando a rota nao tem spec fixa.
const pageSpec = ref(null);
const setPageSpec = spec => {
  pageSpec.value = spec;
};

const persist = () => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        businessHours: state.businessHours,
        scopes: state.scopes,
      })
    );
  } catch {
    // storage indisponivel (modo privado) — contexto vive so na sessao
  }
};

const range = computed(() => {
  if (state.periodKey === 'custom' && state.customFrom && state.customTo) {
    return {
      from: startOfDay(new Date(`${state.customFrom}T00:00:00`)),
      to: endOfDay(new Date(`${state.customTo}T00:00:00`)),
    };
  }
  const preset =
    PERIOD_PRESETS.find(p => p.key === state.periodKey) ||
    PERIOD_PRESETS.find(p => p.key === DEFAULT_PERIOD);
  const [from, to] = preset.range(new Date());
  return { from: startOfDay(from), to: endOfDay(to) };
});

// Selo do chip: "padrao" ate alguem mexer; qualquer outra escolha vira
// "personalizado" e vale para todas as abas (o contexto e global).
const isDefaultPeriod = computed(() => state.periodKey === DEFAULT_PERIOD);

const resetPeriod = () => {
  state.periodKey = DEFAULT_PERIOD;
  state.customFrom = null;
  state.customTo = null;
};

const fromUnix = computed(() => getUnixTime(range.value.from));
const toUnix = computed(() => getUnixTime(range.value.to));

const setPeriod = key => {
  state.periodKey = key;
};

const setCustomPeriod = (from, to) => {
  state.customFrom = from;
  state.customTo = to;
  state.periodKey = 'custom';
};

const setBusinessHours = value => {
  state.businessHours = value;
  persist();
};

// value = {id, name} ou null (limpar)
const setScope = (key, value) => {
  state.scopes[key] = value;
  persist();
};

const setGroupBy = value => {
  state.groupBy = value;
  persist();
};

export function useReportsContext() {
  return {
    state,
    range,
    fromUnix,
    toUnix,
    isDefaultPeriod,
    resetPeriod,
    pageSpec,
    setPageSpec,
    setPeriod,
    setCustomPeriod,
    setBusinessHours,
    setScope,
    setGroupBy,
  };
}
